import type { ReactNode } from "react";
import {
  Bell,
  Bookmark,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Crown,
  Eye,
  FileCheck2,
  Flag,
  Flame,
  Gift,
  Heart,
  ImagePlus,
  Leaf,
  LockKeyhole,
  MessageCircle,
  MessageSquare,
  PenLine,
  Plus,
  Search,
  Send,
  Share2,
  ShieldCheck,
  Smile,
  Sparkles,
  Sprout,
  UserRoundCheck,
  UserRoundPlus,
  UserRound,
  Users,
} from "lucide-react";
import type { StoryboardScreenId } from "./storyboardData";

export function PhoneFrame({
  screenId,
  className = "",
  chrome = "light",
  scale = 1,
}: {
  screenId: StoryboardScreenId;
  className?: string;
  chrome?: "light" | "dark";
  scale?: number;
}) {
  return (
    <div
      className={["mx-auto", className].join(" ")}
      style={{ width: `${390 * scale}px`, height: `${844 * scale}px` }}
    >
      <div
        className={[
          "h-[844px] w-[390px] origin-top-left overflow-hidden rounded-[60px] border p-[10px]",
          "shadow-[0_42px_110px_-46px_rgba(0,0,0,0.85),inset_0_1px_0_rgba(255,255,255,0.18)] ring-1 ring-black/10",
          chrome === "dark"
            ? "border-white/18 bg-[linear-gradient(145deg,#4a4337_0%,#070707_34%,#14110e_68%,#5c472b_100%)]"
            : "border-[#ded4c2] bg-[linear-gradient(145deg,#4a4337_0%,#070707_42%,#4f3d25_100%)]",
        ].join(" ")}
        style={{ transform: `scale(${scale})` }}
      >
        <div className="relative h-full overflow-hidden rounded-[48px] bg-[#fffdf8] text-[#171717] ring-1 ring-black/25">
          <StatusBar />
          <ScreenContent screenId={screenId} />
        </div>
      </div>
    </div>
  );
}

function ScreenContent({ screenId }: { screenId: StoryboardScreenId }) {
  switch (screenId) {
    case "welcome":
      return <WelcomeScreen />;
    case "auth":
      return <AuthScreen />;
    case "onboarding":
      return <OnboardingScreen />;
    case "verse":
      return <VerseRevealScreen />;
    case "reflection":
      return <ReflectionScreen />;
    case "chat":
      return <ChatScreen />;
    case "bible":
      return <BibleReaderScreen />;
    case "vine":
      return <VineHomeScreen />;
    case "assessment":
      return <AssessmentScreen />;
    case "fruit":
      return <FruitTrackerScreen />;
    case "groups":
      return <GroupsScreen />;
    case "community":
      return <CommunityScreen />;
    case "social":
      return <SocialFeedScreen />;
    case "content":
      return <ContentLibraryScreen />;
    case "comments":
      return <CommentsScreen />;
    case "activity":
      return <ActivityScreen />;
    case "church":
      return <ChurchChallengeScreen />;
    case "churchAdmin":
      return <ChurchAdminScreen />;
    case "giving":
      return <GivingScreen />;
    case "journal":
      return <JournalScreen />;
    case "share":
      return <ShareScreen />;
    case "reading":
      return <ReadingPlanScreen />;
    case "paywall":
      return <PaywallScreen />;
  }
}

function StatusBar() {
  return (
    <div className="relative z-20 flex h-11 items-center justify-between px-6 pt-1 text-[10px] font-semibold text-[#282016]">
      <span className="pt-1">9:41</span>
      <span className="absolute left-1/2 top-2 h-6 w-24 -translate-x-1/2 rounded-full bg-[#0e0d0c] shadow-[inset_0_1px_1px_rgba(255,255,255,0.12)]" />
      <div className="flex items-center gap-1">
        <span className="h-1.5 w-4 rounded-full bg-[#282016]" />
        <span className="h-1.5 w-1.5 rounded-full bg-[#282016]" />
      </div>
    </div>
  );
}

function AppHeader({ title, icon }: { title: string; icon?: ReactNode }) {
  return (
    <div className="flex items-center justify-between px-5 pb-4 pt-1">
      <div>
        <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Jesus Entered the Chat</p>
        <h3 className="mt-1 text-lg font-semibold text-[#1c2234]">{title}</h3>
      </div>
      <div className="flex items-center gap-2">
        <button className="grid h-8 w-8 place-items-center rounded-full bg-white text-[#9a7942] shadow-sm" aria-label="Notifications">
          <Bell size={15} />
        </button>
        <button className="grid h-8 w-8 place-items-center rounded-full bg-[#007AFF] text-white shadow-sm" aria-label="Profile">
          {icon ?? <UserRound size={15} />}
        </button>
      </div>
    </div>
  );
}

function BottomNav({ active }: { active: "home" | "chat" | "groups" | "community" }) {
  const items = [
    { id: "home", label: "Home", icon: <Sprout size={15} /> },
    { id: "chat", label: "Chat", icon: <MessageCircle size={15} /> },
    { id: "groups", label: "Circle", icon: <Users size={15} /> },
    { id: "community", label: "Feed", icon: <Heart size={15} /> },
  ] as const;

  return (
    <div className="absolute inset-x-4 bottom-4 grid grid-cols-4 rounded-2xl border border-[#eadfcd] bg-white/92 p-1.5 shadow-[0_14px_38px_-24px_rgba(26,24,18,0.5)] backdrop-blur">
      {items.map((item) => (
        <div
          key={item.id}
          className={[
            "flex h-11 flex-col items-center justify-center gap-0.5 rounded-xl text-[9px]",
            active === item.id ? "bg-[#007AFF] text-white" : "text-[#8b806f]",
          ].join(" ")}
        >
          {item.icon}
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function PrimaryButton({ children, icon }: { children: ReactNode; icon?: ReactNode }) {
  return (
    <button className="flex h-11 w-full items-center justify-center gap-2 rounded-2xl bg-[#007AFF] px-4 text-sm font-semibold text-white shadow-[0_16px_34px_-24px_rgba(0,122,255,0.7)]">
      {icon}
      {children}
    </button>
  );
}

function WelcomeScreen() {
  return (
    <div className="h-full bg-[#fffdf8] px-5 pb-8 pt-1">
      <div className="relative mt-2 overflow-hidden rounded-[34px] bg-[#101827] p-5 text-white shadow-[0_24px_60px_-35px_rgba(16,24,39,0.85)]">
        <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-[#d9a441]/25 blur-xl" />
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#f4d18d]">Jesus Entered the Chat</p>
        <h1 className="mt-14 max-w-[230px] text-4xl font-semibold leading-[0.98]">
          Walk with the Word today.
        </h1>
        <p className="mt-4 max-w-[230px] text-sm leading-6 text-white/72">
          A daily verse, a prayerful companion, and one faithful next step.
        </p>
        <div className="mt-8 grid grid-cols-3 gap-2">
          {[
            [<BookOpen size={15} />, "Word"],
            [<MessageCircle size={15} />, "Chat"],
            [<Sprout size={15} />, "Grow"],
          ].map(([icon, label]) => (
            <div key={String(label)} className="rounded-2xl bg-white/10 p-3 text-center text-[10px] font-semibold text-white/76">
              <span className="mx-auto mb-2 grid h-8 w-8 place-items-center rounded-full bg-white/12 text-[#f4d18d]">{icon}</span>
              {label}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 space-y-3">
        <PrimaryButton icon={<Sparkles size={16} />}>Begin today</PrimaryButton>
        <button className="flex h-11 w-full items-center justify-center rounded-2xl border border-[#eadfcd] bg-white text-sm font-semibold text-[#007AFF]">
          I already have an account
        </button>
      </div>

      <p className="mt-5 text-center text-xs leading-5 text-[#8b806f]">
        Scripture stays visible. Private reflections stay private.
      </p>
    </div>
  );
}

function AuthScreen() {
  return (
    <div className="h-full bg-[#fffdf8] px-5 pb-8 pt-1">
      <div className="mt-3 rounded-[34px] border border-[#eadfcd] bg-white p-5 shadow-soft">
        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#007AFF] text-white">
          <ShieldCheck size={22} />
        </div>
        <h2 className="mt-8 text-3xl font-semibold leading-tight text-[#121823]">Save your walk.</h2>
        <p className="mt-3 text-sm leading-6 text-[#6e6657]">
          Keep your verses, prayers, circles, and progress synced privately.
        </p>

        <div className="mt-7 space-y-3">
          <label className="block">
            <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#9a7942]">Email</span>
            <span className="block rounded-2xl border border-[#eadfcd] bg-[#fffdf8] px-4 py-3 text-sm text-[#8b806f]">
              you@example.com
            </span>
          </label>
          <button className="h-12 w-full rounded-2xl bg-[#007AFF] text-sm font-semibold text-white">Continue</button>
          <button className="h-12 w-full rounded-2xl border border-[#eadfcd] bg-white text-sm font-semibold text-[#007AFF]">
            Continue with Apple
          </button>
        </div>
      </div>

      <p className="mt-5 text-center text-xs leading-5 text-[#8b806f]">No public profile is created by default.</p>
    </div>
  );
}

function OnboardingScreen() {
  const fruits = ["Love", "Joy", "Peace", "Patience", "Kindness", "Faithfulness"];
  const sprintModes = [
    ["Daily", "1 day"],
    ["Weekly", "7 days"],
    ["Monthly", "30 days"],
  ] as const;

  return (
    <div className="h-full bg-[radial-gradient(circle_at_top,#ffffff_0%,#fffdf8_54%,#f8f1e6_100%)] px-5 pb-24 pt-1">
      <div className="mb-4 flex justify-center">
        <div className="h-1.5 w-16 rounded-full bg-[#ddcaa8]" />
      </div>
      <p className="text-[11px] font-semibold uppercase text-[#a57a33]">Sprint setup</p>
      <h2 className="mt-2 text-[27px] font-semibold leading-[1.05] text-[#171717]">
        How long do you want to grow?
      </h2>
      <p className="mt-3 text-sm leading-6 text-[#706756]">
        Pick a Fruit and a path. The app brings verses, resources, and people walking with you.
      </p>

      <div className="mt-5 grid grid-cols-3 gap-2">
        {sprintModes.map(([mode, duration]) => {
          const selected = mode === "Weekly";
          return (
            <button
              key={mode}
              className={[
                "rounded-[20px] border px-2 py-3 text-left transition",
                selected ? "border-[#d9a441] bg-[#fff8e7] shadow-soft" : "border-[#eadfcd] bg-white",
              ].join(" ")}
            >
              <span className={selected ? "block text-xs font-semibold text-[#007AFF]" : "block text-xs font-semibold text-[#332b20]"}>
                {mode}
              </span>
              <span className="mt-1 block text-[10px] font-semibold uppercase text-[#9a7942]">{duration}</span>
            </button>
          );
        })}
      </div>

      <p className="mt-5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#a57a33]">Fruit focus</p>
      <div className="mt-3 grid grid-cols-2 gap-2.5">
        {fruits.map((fruit) => {
          const selected = fruit === "Patience";
          return (
            <button
              key={fruit}
              className={[
                "flex h-12 items-center justify-between rounded-2xl border px-4 text-sm font-semibold",
                selected
                  ? "border-[#d9a441] bg-[#fff8e7] text-[#007AFF] shadow-soft"
                  : "border-[#eadfcd] bg-white text-[#6e6657]",
              ].join(" ")}
            >
              {fruit}
              {selected ? <CheckCircle2 size={17} className="text-[#d39a2d]" /> : null}
            </button>
          );
        })}
      </div>

      <div className="mt-4 rounded-3xl border border-[#eadfcd] bg-white p-4 shadow-soft">
        <p className="text-[10px] font-semibold uppercase text-[#a57a33]">Included in your sprint</p>
        <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] font-semibold text-[#5f5649]">
          {["Daily verses", "Practice prompts", "Resource videos", "Community support"].map((item) => (
            <span key={item} className="rounded-2xl bg-[#f8f2e7] px-3 py-2">{item}</span>
          ))}
        </div>
      </div>

      <div className="absolute inset-x-5 bottom-6">
        <PrimaryButton icon={<Sparkles size={16} />}>Start patience sprint</PrimaryButton>
      </div>
    </div>
  );
}

function VerseRevealScreen() {
  return (
    <div className="h-full bg-[#fbf7ee] pb-20">
      <AppHeader title="Today's word" icon={<Sparkles size={15} />} />
      <div className="px-5">
        <div className="rounded-[26px] bg-[#007AFF] p-5 text-white shadow-[0_24px_55px_-34px_rgba(0,122,255,0.62)]">
          <div className="mb-5 flex items-center justify-between">
            <span className="rounded-full bg-white/12 px-3 py-1 text-[10px] font-semibold uppercase text-[#f4d18d]">
              Patience
            </span>
            <BookOpen size={18} className="text-[#f4d18d]" />
          </div>
          <p className="font-scripture text-[21px] leading-8">
            Be completely humble and gentle; be patient, bearing with one another in love.
          </p>
          <p className="mt-4 text-xs font-semibold uppercase text-[#f4d18d]">Ephesians 4:2</p>
        </div>

        <div className="mt-5 space-y-3">
          <RevealBeat label="Theme" title="Gentleness under pressure" />
          <RevealBeat label="Application" title="Pause before answering the hard message." />
          <RevealBeat label="Prayer" title="Lord, slow my reaction and steady my heart." />
        </div>

        <div className="mt-5 rounded-3xl border border-[#eadfcd] bg-white p-4">
          <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Reflection prompt</p>
          <p className="mt-2 text-sm leading-6 text-[#332b20]">
            Where could patience change one conversation today?
          </p>
        </div>
      </div>
      <BottomNav active="home" />
    </div>
  );
}

function ReflectionScreen() {
  return (
    <div className="h-full bg-[#fffdf8] pb-20">
      <AppHeader title="Reflect" icon={<PenLine size={15} />} />
      <div className="px-5">
        <div className="rounded-[28px] border border-[#eadfcd] bg-white p-5 shadow-soft">
          <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Today's prompt</p>
          <h2 className="mt-3 text-2xl font-semibold leading-tight text-[#007AFF]">
            Where could patience change one conversation?
          </h2>
          <div className="mt-5 min-h-[150px] rounded-3xl bg-[#f8f2e7] p-4">
            <p className="text-sm leading-6 text-[#5d5548]">
              I need to pause before replying to my brother. I can ask one better question before defending myself.
            </p>
          </div>
          <div className="mt-4 flex gap-2">
            {["Private", "Save", "Pray"].map((item) => (
              <span key={item} className="rounded-full bg-[#fff1ce] px-3 py-2 text-[11px] font-semibold text-[#9a7942]">
                {item}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-4 rounded-3xl bg-[#1f563d] p-4 text-white">
          <p className="text-[10px] font-semibold uppercase text-[#f4d18d]">Next step</p>
          <p className="mt-2 text-sm leading-6 text-white/82">Send yourself one reminder before the hard conversation.</p>
        </div>
      </div>
      <BottomNav active="home" />
    </div>
  );
}

function RevealBeat({ label, title }: { label: string; title: string }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-[#eadfcd] bg-white/85 px-4 py-3">
      <span className="grid h-9 w-9 place-items-center rounded-full bg-[#fff1ce] text-[#c58d24]">
        <Sparkles size={15} />
      </span>
      <div>
        <p className="text-[10px] font-semibold uppercase text-[#9a7942]">{label}</p>
        <p className="text-sm font-semibold text-[#252019]">{title}</p>
      </div>
    </div>
  );
}

function ChatScreen() {
  return (
    <div className="h-full bg-[#f8f2e7] pb-20">
      <AppHeader title="Companion" icon={<MessageCircle size={15} />} />
      <div className="space-y-4 px-5">
        <div className="max-w-[82%] rounded-[22px] rounded-tl-md bg-white p-4 shadow-sm">
          <p className="text-sm leading-6 text-[#332b20]">
            I chose Ephesians 4:2 because your last reflection was about patience with family.
          </p>
        </div>
        <div className="ml-auto max-w-[78%] rounded-[22px] rounded-tr-md bg-[#007AFF] p-4 text-white shadow-sm">
          <p className="text-sm leading-6">How do I stay patient when I feel dismissed?</p>
        </div>
        <div className="max-w-[86%] rounded-[22px] rounded-tl-md bg-white p-4 shadow-sm">
          <p className="text-sm leading-6 text-[#332b20]">
            Start with one breath and one honest sentence. Patience is strength under guidance, not silence.
          </p>
          <div className="mt-3 rounded-2xl bg-[#fbf3e3] p-3">
            <p className="text-[11px] font-semibold uppercase text-[#9a7942]">Try this prayer</p>
            <p className="mt-1 text-xs leading-5 text-[#5e5547]">
              Lord, help me listen before defending myself.
            </p>
          </div>
        </div>
      </div>
      <div className="absolute inset-x-5 bottom-20 rounded-2xl border border-[#eadfcd] bg-white p-2">
        <div className="flex items-center gap-2">
          <span className="flex-1 px-3 text-xs text-[#8b806f]">Ask about today&apos;s verse...</span>
          <button className="grid h-9 w-9 place-items-center rounded-xl bg-[#d9a441] text-white" aria-label="Send">
            <Send size={15} />
          </button>
        </div>
      </div>
      <BottomNav active="chat" />
    </div>
  );
}

function BibleReaderScreen() {
  return (
    <div className="h-full bg-[#fffdf8] pb-20">
      <AppHeader title="Bible" icon={<BookOpen size={15} />} />
      <div className="px-5">
        <div className="flex items-center gap-2 rounded-2xl border border-[#eadfcd] bg-white px-4 py-3">
          <Search size={15} className="text-[#9a7942]" />
          <span className="text-sm text-[#7d7364]">Search book, chapter, or verse</span>
        </div>

        <div className="mt-4 rounded-[28px] bg-white p-5 shadow-soft">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Ephesians</p>
              <h2 className="text-2xl font-semibold text-[#121823]">Chapter 4</h2>
            </div>
            <Bookmark size={18} className="text-[#d9a441]" />
          </div>
          <VerseLine number="1" text="Walk in a manner worthy of the calling to which you have been called." />
          <VerseLine number="2" text="With all humility and gentleness, with patience, bearing with one another in love." active />
          <VerseLine number="3" text="Eager to maintain the unity of the Spirit in the bond of peace." />
        </div>

        <div className="mt-4 rounded-3xl bg-[#007AFF] p-4 text-white">
          <p className="text-[10px] font-semibold uppercase text-[#f4d18d]">Why this passage?</p>
          <p className="mt-2 text-xs leading-5 text-white/78">Today's word draws from verse 2.</p>
        </div>
      </div>
      <BottomNav active="home" />
    </div>
  );
}

function VerseLine({ number, text, active = false }: { number: string; text: string; active?: boolean }) {
  return (
    <p
      className={[
        "mb-4 rounded-2xl p-3 font-scripture text-[17px] leading-7",
        active ? "bg-[#fff4d8] text-[#007AFF]" : "text-[#4b4338]",
      ].join(" ")}
    >
      <span className="mr-2 align-super text-[10px] font-semibold text-[#9a7942]">{number}</span>
      {text}
    </p>
  );
}

function VineHomeScreen() {
  return (
    <div className="h-full bg-[#fbf7ee] pb-20">
      <AppHeader title="Good morning" icon={<Leaf size={15} />} />
      <div className="px-5">
        <div className="relative overflow-hidden rounded-[28px] bg-[#1f563d] p-5 text-white shadow-[0_24px_55px_-35px_rgba(31,86,61,0.75)]">
          <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-[#f4d18d]/20" />
          <p className="text-[10px] font-semibold uppercase text-[#f4d18d]">Faith Vine</p>
          <h2 className="mt-2 text-3xl font-semibold">12 days strong</h2>
          <p className="mt-2 max-w-[210px] text-sm leading-6 text-white/82">
            Your connection with God&apos;s word is taking root.
          </p>
          <div className="mt-5 flex items-end gap-2">
            {[36, 52, 72, 96].map((height, index) => (
              <span
                key={height}
                className="w-8 rounded-full bg-[#f4d18d]"
                style={{ height: `${height}px`, opacity: 0.45 + index * 0.13 }}
              />
            ))}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <MiniStat icon={<Flame size={16} />} label="Grace token" value="1 left" />
          <MiniStat icon={<PenLine size={16} />} label="Reflections" value="34 saved" />
        </div>

        <div className="mt-4 rounded-3xl border border-[#eadfcd] bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Current sprint</p>
              <p className="mt-1 text-sm font-semibold text-[#252019]">Weekly patience path</p>
            </div>
            <span className="rounded-full bg-[#fff1ce] px-3 py-1 text-[10px] font-semibold text-[#9a7942]">Day 3/7</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-semibold text-[#6e6657]">
            {["Verse", "Resource", "Circle"].map((item) => (
              <span key={item} className="rounded-2xl bg-[#f8f2e7] px-2 py-2">{item}</span>
            ))}
          </div>
        </div>

        <div className="mt-4 rounded-3xl border border-[#eadfcd] bg-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Next step</p>
              <p className="mt-1 text-sm font-semibold text-[#252019]">Continue today&apos;s walk</p>
            </div>
            <ChevronRight size={18} className="text-[#9a7942]" />
          </div>
        </div>
      </div>
      <BottomNav active="home" />
    </div>
  );
}

function AssessmentScreen() {
  return (
    <div className="h-full bg-[#fffdf8] pb-20">
      <AppHeader title="Assessment" icon={<Smile size={15} />} />
      <div className="px-5">
        <div className="rounded-[28px] bg-[#007AFF] p-5 text-white shadow-[0_24px_55px_-35px_rgba(0,122,255,0.62)]">
          <p className="text-[10px] font-semibold uppercase text-[#f4d18d]">Question 4 of 9</p>
          <h2 className="mt-2 text-2xl font-semibold leading-tight">I pause before reacting when I feel misunderstood.</h2>
        </div>

        <div className="mt-5 space-y-3">
          {["Rarely", "Sometimes", "Often", "Practicing this"].map((answer) => {
            const selected = answer === "Sometimes";
            return (
              <button
                key={answer}
                className={[
                  "flex h-12 w-full items-center justify-between rounded-2xl border px-4 py-3 text-sm font-semibold",
                  selected ? "border-[#d9a441] bg-[#fff8e7] text-[#007AFF]" : "border-[#eadfcd] bg-white text-[#6e6657]",
                ].join(" ")}
              >
                {answer}
                {selected ? <CheckCircle2 size={17} className="text-[#d39a2d]" /> : null}
              </button>
            );
          })}
        </div>

        <div className="mt-5 h-2 overflow-hidden rounded-full bg-[#efe4d3]">
          <div className="h-full w-[44%] rounded-full bg-[#d9a441]" />
        </div>
      </div>
      <BottomNav active="home" />
    </div>
  );
}

function MiniStat({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-3xl border border-[#eadfcd] bg-white p-4">
      <div className="mb-3 grid h-9 w-9 place-items-center rounded-full bg-[#fff1ce] text-[#c58d24]">{icon}</div>
      <p className="text-[10px] font-semibold uppercase text-[#9a7942]">{label}</p>
      <p className="mt-1 text-sm font-semibold text-[#252019]">{value}</p>
    </div>
  );
}

function FruitTrackerScreen() {
  const fruits = [
    ["Love", 78, "Practiced"],
    ["Joy", 66, "Growing"],
    ["Peace", 58, "Steady"],
    ["Patience", 91, "Focus"],
    ["Kindness", 73, "Strong"],
    ["Goodness", 45, "Next"],
  ] as const;

  return (
    <div className="h-full bg-[#fbf7ee] pb-24">
      <AppHeader title="Growth" icon={<Sprout size={15} />} />
      <div className="px-5">
        <div className="rounded-[28px] bg-white p-4 shadow-soft">
          <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Spiritual growth tier</p>
          <div className="mt-3 flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-[#007AFF]">Believer</h2>
            <span className="rounded-full bg-[#fff1ce] px-3 py-1 text-xs font-semibold text-[#9a7942]">68%</span>
          </div>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#efe4d3]">
            <div className="h-full w-[68%] rounded-full bg-[#d9a441]" />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          {fruits.map(([fruit, value, status]) => (
            <div key={fruit} className="flex min-h-[70px] items-center gap-3 rounded-[22px] border border-[#eadfcd] bg-white p-3">
              <Ring value={value} />
              <div className="min-w-0">
                <p className="truncate text-[12px] font-semibold text-[#332b20]">{fruit}</p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9a7942]">{status}</p>
              </div>
            </div>
          ))}
        </div>

      </div>
      <BottomNav active="home" />
    </div>
  );
}

function Ring({ value }: { value: number }) {
  const stroke = `${value}, 100`;
  return (
    <svg viewBox="0 0 36 36" className="h-10 w-10 flex-shrink-0">
      <path
        d="M18 2.5a15.5 15.5 0 1 1 0 31a15.5 15.5 0 0 1 0-31"
        fill="none"
        stroke="#efe4d3"
        strokeWidth="3"
      />
      <path
        d="M18 2.5a15.5 15.5 0 1 1 0 31a15.5 15.5 0 0 1 0-31"
        fill="none"
        stroke="#d9a441"
        strokeDasharray={stroke}
        strokeLinecap="round"
        strokeWidth="3"
      />
    </svg>
  );
}

function GroupsScreen() {
  return (
    <div className="h-full bg-[#fbf7ee] pb-20">
      <AppHeader title="Prayer Circle" icon={<Users size={15} />} />
      <div className="px-5">
        <div className="rounded-[28px] bg-[#007AFF] p-5 text-white shadow-[0_24px_55px_-35px_rgba(0,122,255,0.62)]">
          <p className="text-[10px] font-semibold uppercase text-[#f4d18d]">Family circle</p>
          <h2 className="mt-2 text-3xl font-semibold">5 of 7 days</h2>
          <p className="mt-2 text-sm leading-6 text-white/82">
            Someone in your circle prayed each of these days.
          </p>
          <div className="mt-5 flex -space-x-2">
            {["JB", "SR", "MK", "AM", "+2"].map((avatar, index) => (
              <span
                key={avatar}
                className="grid h-10 w-10 place-items-center rounded-full border-2 border-[#007AFF] bg-[#f4d18d] text-xs font-semibold text-[#007AFF]"
                style={{ opacity: 1 - index * 0.06 }}
              >
                {avatar}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <PrayerCard name="Sophia" text="Praying for patience at work today." />
          <PrayerCard name="Marcus" text="Shared Psalm 23 with the circle." />
          <PrayerCard name="Anika" text="Asked for peace before a family talk." />
        </div>
      </div>
      <BottomNav active="groups" />
    </div>
  );
}

function PrayerCard({ name, text }: { name: string; text: string }) {
  return (
    <div className="rounded-3xl border border-[#eadfcd] bg-white p-4">
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-full bg-[#fff1ce] text-xs font-semibold text-[#9a7942]">
          {name.slice(0, 1)}
        </span>
        <div>
          <p className="text-sm font-semibold text-[#252019]">{name}</p>
          <p className="mt-1 text-xs leading-5 text-[#6e6657]">{text}</p>
        </div>
      </div>
    </div>
  );
}

function CommunityScreen() {
  return (
    <div className="h-full bg-[#f8f2e7] pb-20">
      <AppHeader title="Community" icon={<Heart size={15} />} />
      <div className="space-y-4 px-5">
        <div className="rounded-[28px] bg-[#007AFF] p-5 text-white shadow-[0_24px_55px_-35px_rgba(0,122,255,0.62)]">
          <p className="text-[10px] font-semibold uppercase text-[#f4d18d]">One another</p>
          <h2 className="mt-2 text-2xl font-semibold leading-tight">A Christian feed with guardrails.</h2>
          <p className="mt-3 text-sm leading-6 text-white/78">
            Testimony, prayer, Scripture, photos, and encouragement stay inside reviewed lanes.
          </p>
        </div>

        <div className="rounded-3xl border border-[#eadfcd] bg-white p-4">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#1f563d] text-white">
              <Leaf size={16} />
            </span>
            <div>
              <p className="text-sm font-semibold text-[#252019]">Encouragement prompt</p>
              <p className="text-xs text-[#7d7364]">What verse carried you this week?</p>
            </div>
          </div>
        </div>

        <FeedCard
          name="Maya"
          tag="Testimony"
          text="I paused before a hard reply today. That was new for me."
          count="28 prayed"
        />
      </div>
      <BottomNav active="community" />
    </div>
  );
}

function SocialFeedScreen() {
  return (
    <div className="h-full bg-[#fbf7ee] pb-20">
      <AppHeader title="Community" icon={<Heart size={15} />} />
      <div className="space-y-4 px-5">
        <div className="rounded-full bg-[#efe4d3] p-1">
          <div className="grid grid-cols-2 gap-1 text-center text-xs font-semibold">
            <span className="rounded-full bg-white px-4 py-2 text-[#007AFF] shadow-sm">Everyone</span>
            <span className="rounded-full px-4 py-2 text-[#7d7364]">Following</span>
          </div>
        </div>

        <div className="rounded-[28px] border border-[#eadfcd] bg-white p-4 shadow-soft">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#fff1ce] text-xs font-semibold text-[#9a7942]">
              S
            </span>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-[#252019]">Sophia</p>
                  <p className="text-[11px] text-[#7d7364]">Growing in Patience</p>
                </div>
                <span className="flex items-center gap-1 rounded-full bg-[#edf3ed] px-3 py-1.5 text-[10px] font-semibold text-[#1f563d]">
                  <UserRoundCheck size={12} />
                  Following
                </span>
              </div>
              <p className="mt-3 text-sm leading-6 text-[#4b4338]">
                I chose a softer answer today. Ephesians 4:2 stayed with me.
              </p>
            </div>
          </div>
          <div className="mt-4 h-36 rounded-3xl bg-[linear-gradient(135deg,#007AFF_0%,#315a45_54%,#d9a441_100%)] p-4 text-white">
            <p className="font-scripture text-[18px] leading-7">Be patient, bearing with one another in love.</p>
            <p className="mt-3 text-[10px] font-semibold uppercase text-[#f4d18d]">Ephesians 4:2</p>
          </div>
          <div className="mt-4 flex items-center justify-between text-[11px] font-semibold text-[#7d7364]">
            <span className="flex items-center gap-1.5"><Heart size={14} /> 42 encouraged</span>
            <span className="flex items-center gap-1.5"><MessageSquare size={14} /> 9 replies</span>
            <span className="flex items-center gap-1.5"><Flag size={13} /> Report</span>
          </div>
        </div>

        <div className="rounded-[28px] border border-[#eadfcd] bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-[#252019]">Share encouragement</p>
            <span className="rounded-full bg-[#fff1ce] px-3 py-1 text-[10px] font-semibold text-[#9a7942]">Reviewed</span>
          </div>
          <p className="rounded-2xl bg-[#f8f2e7] px-4 py-3 text-sm text-[#8b806f]">
            Share a small win, prayer, verse, or photo...
          </p>
          <div className="mt-3 flex items-center justify-between">
            <button className="grid h-10 w-10 place-items-center rounded-full bg-[#f3eadc] text-[#7d7364]" aria-label="Add image">
              <ImagePlus size={17} />
            </button>
            <button className="rounded-full bg-[#007AFF] px-5 py-2 text-sm font-semibold text-white">Share</button>
          </div>
        </div>
      </div>
      <BottomNav active="community" />
    </div>
  );
}

function ContentLibraryScreen() {
  return (
    <div className="h-full bg-[#fffdf8] pb-20">
      <AppHeader title="Content" icon={<FileCheck2 size={15} />} />
      <div className="space-y-4 px-5">
        <div className="rounded-[28px] bg-[#1f563d] p-5 text-white shadow-[0_24px_55px_-35px_rgba(31,86,61,0.75)]">
          <p className="text-[10px] font-semibold uppercase text-[#f4d18d]">Approved library</p>
          <h2 className="mt-2 text-2xl font-semibold leading-tight">Christian content we can use.</h2>
          <p className="mt-3 text-sm leading-6 text-white/78">
            Public-domain Scripture, original prompts, owned art, and advisor-reviewed packs.
          </p>
        </div>

        {[
          ["WEB Bible", "Public domain scripture", "Ready"],
          ["Verse cards", "Owned templates and generated art", "Ready"],
          ["Devotional prompts", "Original writing by theme", "Review"],
          ["Partner media", "Licensed Christian creators", "Planned"],
        ].map(([title, detail, status]) => (
          <div key={title} className="flex items-center gap-3 rounded-3xl border border-[#eadfcd] bg-white p-4 shadow-sm">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#fff1ce] text-[#c58d24]">
              {status === "Ready" ? <CheckCircle2 size={17} /> : status === "Review" ? <Eye size={17} /> : <LockKeyhole size={16} />}
            </span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#252019]">{title}</p>
              <p className="mt-1 text-xs leading-5 text-[#6e6657]">{detail}</p>
            </div>
            <span className="rounded-full bg-[#f3eadc] px-3 py-1 text-[10px] font-semibold text-[#806b47]">{status}</span>
          </div>
        ))}
      </div>
      <BottomNav active="community" />
    </div>
  );
}

function CommentsScreen() {
  return (
    <div className="h-full bg-[#fbf7ee] pb-20">
      <AppHeader title="Replies" icon={<MessageSquare size={15} />} />
      <div className="space-y-4 px-5">
        <div className="rounded-[28px] border border-[#eadfcd] bg-white p-4 shadow-soft">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-[#fff1ce] text-xs font-semibold text-[#9a7942]">S</span>
            <div>
              <p className="text-sm font-semibold text-[#252019]">Sophia</p>
              <p className="text-[11px] text-[#7d7364]">Ephesians 4:2</p>
            </div>
          </div>
          <p className="mt-3 text-sm leading-6 text-[#4b4338]">
            I chose a softer answer today. Ephesians 4:2 stayed with me.
          </p>
        </div>

        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#9a7942]">Encouraging replies</p>
        <CommentBubble name="Marcus" text="That is a real fruit of the Spirit moment. Praying it keeps growing." />
        <CommentBubble name="Ari" text="Needed this reminder before a family call tonight." />

        <div className="rounded-[28px] border border-[#eadfcd] bg-white p-4">
          <p className="rounded-2xl bg-[#f8f2e7] px-4 py-3 text-sm text-[#8b806f]">Add an encouraging reply...</p>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-[#8b806f]">Comments are moderated.</span>
            <button className="grid h-10 w-10 place-items-center rounded-full bg-[#007AFF] text-white" aria-label="Send reply">
              <Send size={15} />
            </button>
          </div>
        </div>
      </div>
      <BottomNav active="community" />
    </div>
  );
}

function ActivityScreen() {
  return (
    <div className="h-full bg-[#fffdf8] pb-20">
      <AppHeader title="Activity" icon={<Bell size={15} />} />
      <div className="space-y-4 px-5">
        <div className="rounded-[28px] bg-[#007AFF] p-5 text-white shadow-[0_24px_55px_-35px_rgba(0,122,255,0.62)]">
          <p className="text-[10px] font-semibold uppercase text-[#f4d18d]">Community loop</p>
          <h2 className="mt-2 text-2xl font-semibold leading-tight">Signals without noise.</h2>
          <p className="mt-3 text-sm leading-6 text-white/78">
            Follows, replies, encouragement, group nudges, and moderation updates stay readable.
          </p>
        </div>

        <ActivityItem icon={<Heart size={15} />} title="Maya encouraged your verse card" detail="2 minutes ago" />
        <ActivityItem icon={<MessageSquare size={15} />} title="Caleb replied with James 1:19" detail="12 minutes ago" />
        <ActivityItem icon={<UserRoundPlus size={15} />} title="Sophia started following you" detail="Today" />
        <ActivityItem icon={<ShieldCheck size={15} />} title="Your post was approved" detail="Visible in Community" />
      </div>
      <BottomNav active="community" />
    </div>
  );
}

function ChurchChallengeScreen() {
  return (
    <div className="h-full bg-[#fbf7ee] pb-20">
      <AppHeader title="Church" icon={<Users size={15} />} />
      <div className="space-y-4 px-5">
        <div className="rounded-[28px] bg-[#1f563d] p-5 text-white shadow-[0_24px_55px_-35px_rgba(31,86,61,0.75)]">
          <div className="mb-4 flex items-center justify-between">
            <span className="rounded-full bg-white/12 px-3 py-1 text-[10px] font-semibold uppercase text-[#f4d18d]">
              Grace Community
            </span>
            <ShieldCheck size={18} className="text-[#f4d18d]" />
          </div>
          <h2 className="text-2xl font-semibold leading-tight">7 days of practical patience</h2>
          <p className="mt-3 text-sm leading-6 text-white/80">
            A church-led challenge for your small group, parish, or Sunday gathering.
          </p>
          <div className="mt-5 grid grid-cols-3 gap-2 text-center text-[10px] font-semibold text-white/80">
            {["Verse", "Practice", "Share"].map((step) => (
              <span key={step} className="rounded-2xl bg-white/12 px-2 py-3">{step}</span>
            ))}
          </div>
        </div>

        <div className="rounded-[28px] border border-[#eadfcd] bg-white p-4 shadow-soft">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Today&apos;s church prompt</p>
              <p className="mt-1 text-sm font-semibold text-[#252019]">Serve one person before being asked.</p>
            </div>
            <span className="rounded-full bg-[#fff1ce] px-3 py-1 text-[10px] font-semibold text-[#9a7942]">Day 2</span>
          </div>
          <p className="text-sm leading-6 text-[#6e6657]">
            Invite your circle or church group to practice this with you.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button className="rounded-2xl bg-[#007AFF] px-4 py-3 text-xs font-semibold text-white">Join challenge</button>
            <button className="flex items-center justify-center gap-1.5 rounded-2xl border border-[#eadfcd] bg-white px-4 py-3 text-xs font-semibold text-[#007AFF]">
              <Share2 size={14} />
              Share
            </button>
          </div>
        </div>

        <div className="rounded-3xl border border-[#eadfcd] bg-white p-4">
          <p className="text-[10px] font-semibold uppercase text-[#9a7942]">People walking with you</p>
          <div className="mt-3 flex items-center justify-between">
            <div className="flex -space-x-2">
              {["M", "J", "A", "K", "+18"].map((avatar) => (
                <span key={avatar} className="grid h-9 w-9 place-items-center rounded-full border-2 border-white bg-[#fff1ce] text-[10px] font-semibold text-[#9a7942]">
                  {avatar}
                </span>
              ))}
            </div>
            <span className="text-xs font-semibold text-[#1f563d]">23 joined</span>
          </div>
        </div>
      </div>
      <BottomNav active="groups" />
    </div>
  );
}

function ChurchAdminScreen() {
  return (
    <div className="h-full bg-[#fffdf8] pb-20">
      <AppHeader title="Upload" icon={<FileCheck2 size={15} />} />
      <div className="space-y-4 px-5">
        <div className="rounded-[28px] border border-[#eadfcd] bg-white p-5 shadow-soft">
          <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Church partner portal</p>
          <h2 className="mt-2 text-2xl font-semibold leading-tight text-[#121823]">Create a challenge</h2>
          <div className="mt-5 space-y-3">
            <div className="rounded-2xl bg-[#f8f2e7] px-4 py-3">
              <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Title</p>
              <p className="mt-1 text-sm font-semibold text-[#332b20]">Serve with patience this week</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-[#f8f2e7] px-4 py-3">
                <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Days</p>
                <p className="mt-1 text-sm font-semibold text-[#332b20]">7</p>
              </div>
              <div className="rounded-2xl bg-[#f8f2e7] px-4 py-3">
                <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Audience</p>
                <p className="mt-1 text-sm font-semibold text-[#332b20]">Church</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-[#eadfcd] bg-white p-4">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#fff1ce] text-[#c58d24]">
              <ImagePlus size={17} />
            </span>
            <div>
              <p className="text-sm font-semibold text-[#252019]">Add verse, image, or short video</p>
              <p className="mt-1 text-xs leading-5 text-[#6e6657]">Owned or licensed content only.</p>
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-[#101827] p-4 text-white">
          <div className="mb-3 flex items-center gap-2 text-[#f4d18d]">
            <ShieldCheck size={15} />
            <p className="text-[10px] font-semibold uppercase">Review required</p>
          </div>
          <p className="text-sm leading-6 text-white/78">
            Challenges become visible after moderation, rights checks, and faith-advisor rules pass.
          </p>
        </div>

        <PrimaryButton icon={<FileCheck2 size={16} />}>Submit for review</PrimaryButton>
      </div>
      <BottomNav active="groups" />
    </div>
  );
}

function GivingScreen() {
  return (
    <div className="h-full bg-[#fbf7ee] pb-20">
      <AppHeader title="Give" icon={<Gift size={15} />} />
      <div className="space-y-4 px-5">
        <div className="rounded-[28px] bg-[#101827] p-5 text-white shadow-[0_26px_60px_-35px_rgba(16,24,39,0.9)]">
          <p className="text-[10px] font-semibold uppercase text-[#f4d18d]">Tithe and impact</p>
          <h2 className="mt-2 text-3xl font-semibold leading-tight">Give with clarity.</h2>
          <p className="mt-3 text-sm leading-6 text-white/76">
            Local church giving, approved causes, receipts, and the app&apos;s own give-back are separated.
          </p>
        </div>

        <div className="grid gap-3">
          <GivingOption
            title="My church tithe"
            detail="Verified church profile and receipt path required."
            status="Church review"
          />
          <GivingOption
            title="Good cause fund"
            detail="Approved nonprofit or ministry cause with clear use of funds."
            status="Approval gate"
          />
          <GivingOption
            title="JETC give-back"
            detail="A visible portion of app proceeds can be set aside for causes."
            status="Disclosure"
          />
        </div>

        <div className="rounded-3xl border border-[#eadfcd] bg-white p-4">
          <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Trust controls</p>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] font-semibold text-[#6e6657]">
            {["Receipts", "Cause details", "Payment review", "Payout audit"].map((item) => (
              <span key={item} className="rounded-2xl bg-[#f8f2e7] px-3 py-2 text-center">{item}</span>
            ))}
          </div>
        </div>
      </div>
      <BottomNav active="community" />
    </div>
  );
}

function GivingOption({ title, detail, status }: { title: string; detail: string; status: string }) {
  return (
    <div className="rounded-3xl border border-[#eadfcd] bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[#252019]">{title}</p>
          <p className="mt-1 text-xs leading-5 text-[#6e6657]">{detail}</p>
        </div>
        <span className="whitespace-nowrap rounded-full bg-[#fff1ce] px-3 py-1 text-[10px] font-semibold text-[#9a7942]">
          {status}
        </span>
      </div>
    </div>
  );
}

function JournalScreen() {
  return (
    <div className="h-full bg-[#fffdf8] pb-20">
      <AppHeader title="Journal" icon={<PenLine size={15} />} />
      <div className="space-y-4 px-5">
        <div className="rounded-[28px] bg-[#007AFF] p-5 text-white shadow-[0_24px_55px_-35px_rgba(0,122,255,0.62)]">
          <p className="text-[10px] font-semibold uppercase text-[#f4d18d]">Prayer saved</p>
          <h2 className="mt-2 text-2xl font-semibold leading-tight">What God is teaching me</h2>
          <p className="mt-3 text-sm leading-6 text-white/78">
            Patience is not silence. It is choosing a Spirit-led response.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <JournalTile icon={<BookOpen size={16} />} label="SOAP" value="8 notes" />
          <JournalTile icon={<Heart size={16} />} label="Prayer" value="12 saved" />
          <JournalTile icon={<Smile size={16} />} label="Gratitude" value="19 gifts" />
          <JournalTile icon={<PenLine size={16} />} label="Freeform" value="5 drafts" />
        </div>

        <button className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-[#eadfcd] bg-white text-sm font-semibold text-[#007AFF]">
          <Plus size={16} />
          New entry
        </button>
      </div>
      <BottomNav active="home" />
    </div>
  );
}

function JournalTile({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-3xl border border-[#eadfcd] bg-white p-4">
      <div className="mb-4 grid h-10 w-10 place-items-center rounded-full bg-[#fff1ce] text-[#c58d24]">{icon}</div>
      <p className="text-sm font-semibold text-[#252019]">{label}</p>
      <p className="mt-1 text-xs text-[#7d7364]">{value}</p>
    </div>
  );
}

function ShareScreen() {
  return (
    <div className="h-full bg-[#f8f2e7] pb-20">
      <AppHeader title="Share" icon={<Share2 size={15} />} />
      <div className="px-5">
        <div className="rounded-[32px] bg-[#101827] p-5 text-white shadow-[0_26px_60px_-35px_rgba(16,24,39,0.9)]">
          <div className="rounded-[26px] bg-[#fffdf8] p-5 text-[#161616]">
            <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Ephesians 4:2</p>
            <p className="mt-5 font-scripture text-[23px] leading-8">
              Be completely humble and gentle; be patient, bearing with one another in love.
            </p>
            <div className="mt-6 flex items-center justify-between border-t border-[#eadfcd] pt-4">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#007AFF]">JETC</span>
              <Bookmark size={16} className="text-[#d9a441]" />
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3">
          {["Text", "Story", "Circle"].map((item) => (
            <button key={item} className="rounded-2xl border border-[#eadfcd] bg-white px-3 py-3 text-xs font-semibold text-[#007AFF]">
              {item}
            </button>
          ))}
        </div>

        <div className="mt-5 rounded-3xl border border-[#eadfcd] bg-white p-4">
          <p className="text-[10px] font-semibold uppercase text-[#9a7942]">Privacy</p>
          <p className="mt-2 text-xs leading-5 text-[#6e6657]">Only the verse card is shared. Your journal stays private.</p>
        </div>
      </div>
      <BottomNav active="home" />
    </div>
  );
}

function ReadingPlanScreen() {
  return (
    <div className="h-full bg-[#fffdf8] pb-20">
      <AppHeader title="Reading Plan" icon={<CalendarDays size={15} />} />
      <div className="px-5">
        <div className="rounded-[28px] bg-[#1f563d] p-5 text-white shadow-[0_24px_55px_-35px_rgba(31,86,61,0.75)]">
          <p className="text-[10px] font-semibold uppercase text-[#f4d18d]">7 day path</p>
          <h2 className="mt-2 text-3xl font-semibold leading-tight">Peace in the hard conversation</h2>
          <p className="mt-3 text-sm leading-6 text-white/78">Short readings, one reflection, one prayer.</p>
          <div className="mt-5 grid grid-cols-7 gap-1.5">
            {Array.from({ length: 7 }).map((_, index) => (
              <span
                key={index}
                className={[
                  "h-2 rounded-full",
                  index < 3 ? "bg-[#f4d18d]" : "bg-white/22",
                ].join(" ")}
              />
            ))}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {[
            { day: "Day 1", passage: "Ephesians 4", done: true },
            { day: "Day 2", passage: "James 1", done: true },
            { day: "Day 3", passage: "Proverbs 15", done: true },
            { day: "Day 4", passage: "Colossians 3", done: false },
          ].map(({ day, passage, done }) => (
            <div key={day} className="flex items-center justify-between rounded-2xl border border-[#eadfcd] bg-white px-4 py-3">
              <div>
                <p className="text-[10px] font-semibold uppercase text-[#9a7942]">{day}</p>
                <p className="text-sm font-semibold text-[#252019]">{passage}</p>
              </div>
              <span className={done ? "text-[#1f563d]" : "text-[#c9b99c]"}>
                {done ? <CheckCircle2 size={18} /> : <LockKeyhole size={17} />}
              </span>
            </div>
          ))}
        </div>
      </div>
      <BottomNav active="home" />
    </div>
  );
}

function PaywallScreen() {
  return (
    <div className="h-full bg-[#fffdf8] px-5 pb-8 pt-1">
      <div className="mt-2 rounded-[34px] bg-[#101827] p-5 text-white shadow-[0_26px_60px_-35px_rgba(16,24,39,0.9)]">
        <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[#d9a441] text-[#17110a]">
          <Crown size={24} />
        </div>
        <p className="mt-8 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#f4d18d]">Premium</p>
        <h2 className="mt-2 text-3xl font-semibold leading-tight">Go deeper with your daily walk.</h2>
        <p className="mt-3 text-sm leading-6 text-white/72">
          More journals, guided plans, prayer circles, and unlimited companion conversations.
        </p>
      </div>

      <div className="mt-5 space-y-3">
        {[
          [<Gift size={16} />, "7 day free trial"],
          [<ShieldCheck size={16} />, "Private by design"],
          [<MessageCircle size={16} />, "Unlimited scripture chat"],
        ].map(([icon, label]) => (
          <div key={String(label)} className="flex items-center gap-3 rounded-2xl border border-[#eadfcd] bg-white px-4 py-3">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-[#fff1ce] text-[#c58d24]">{icon}</span>
            <span className="text-sm font-semibold text-[#252019]">{label}</span>
          </div>
        ))}
      </div>

      <div className="absolute inset-x-5 bottom-6">
        <PrimaryButton icon={<Crown size={16} />}>Start free trial</PrimaryButton>
        <button className="mt-3 h-10 w-full text-sm font-semibold text-[#6e6657]">Not now</button>
      </div>
    </div>
  );
}

function FeedCard({ name, tag, text, count }: { name: string; tag: string; text: string; count: string }) {
  return (
    <div className="rounded-3xl border border-[#eadfcd] bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-[#fff1ce] text-xs font-semibold text-[#9a7942]">
            {name.slice(0, 1)}
          </span>
          <div>
            <p className="text-sm font-semibold text-[#252019]">{name}</p>
            <p className="text-[10px] font-semibold uppercase text-[#9a7942]">{tag}</p>
          </div>
        </div>
        <Heart size={16} className="text-[#c58d24]" />
      </div>
      <p className="text-sm leading-6 text-[#4b4338]">{text}</p>
      <p className="mt-3 text-[11px] font-semibold text-[#827766]">{count}</p>
    </div>
  );
}

function CommentBubble({ name, text }: { name: string; text: string }) {
  return (
    <div className="rounded-3xl border border-[#eadfcd] bg-white p-4">
      <div className="flex items-start gap-3">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-[#fff1ce] text-xs font-semibold text-[#9a7942]">
          {name.slice(0, 1)}
        </span>
        <div>
          <p className="text-sm font-semibold text-[#252019]">{name}</p>
          <p className="mt-1 text-xs leading-5 text-[#6e6657]">{text}</p>
        </div>
      </div>
    </div>
  );
}

function ActivityItem({ icon, title, detail }: { icon: ReactNode; title: string; detail: string }) {
  return (
    <div className="flex items-center gap-3 rounded-3xl border border-[#eadfcd] bg-white p-4 shadow-sm">
      <span className="grid h-10 w-10 place-items-center rounded-full bg-[#fff1ce] text-[#c58d24]">{icon}</span>
      <div>
        <p className="text-sm font-semibold text-[#252019]">{title}</p>
        <p className="mt-1 text-xs text-[#7d7364]">{detail}</p>
      </div>
    </div>
  );
}
